import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import {
  PARTNER_ORGANIZATION_HAS_CHILDREN_ERROR,
  type PartnerOrgLink,
  assertPartnerOrganizationParentChange,
} from "@shared/helpers/partner-organization-hierarchy";
import { Roles } from "@shared/Roles";
import { remult } from "remult";
import { PartnerOrganization } from "./entity";

/** Ancestors of the new parent, plus descendants of the org being saved. */
async function rowsForParentCheck(
  orgId: string | null,
  parentId: string,
): Promise<PartnerOrgLink[]> {
  const organizationRepo = remult.repo(PartnerOrganization);
  const rows: PartnerOrgLink[] = [];
  const seen = new Set<string>();
  const add = (id: string, nextParentId: string | null) => {
    if (seen.has(id)) return;
    seen.add(id);
    rows.push({ id, parentId: nextParentId });
  };

  let current: string | null = parentId;
  const walked = new Set<string>();
  for (let step = 0; current && step < 8; step += 1) {
    if (walked.has(current)) break;
    walked.add(current);
    const node = await organizationRepo.findId(current);
    if (!node) break;
    add(node.id, node.parentId ?? null);
    current = node.parentId ?? null;
  }

  if (!orgId) return rows;

  let frontier = [orgId];
  const visited = new Set<string>([orgId]);
  for (let depth = 0; frontier.length > 0 && depth < 8; depth += 1) {
    const children = await organizationRepo.find({
      where: { parentId: { $in: frontier } },
    });
    frontier = [];
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      add(child.id, child.parentId ?? null);
      frontier.push(child.id);
    }
  }

  return rows;
}

export const PartnerOrganizationEntityConfig: HyperEntityConfig<PartnerOrganization> =
  {
    allowApiRead: true,
    allowApiCrud: Roles.SuperAdmins,

    apiPrefilterPerRole: (filter) => {
      if (!remult.user) return;

      filter(Roles.SuperDuperAdmin, true);
      filter(Roles.SuperAdmin, true);

      filter([Roles.Admin, Roles.Manager], {
        organizationManagers: {
          $some: {
            managerId: remult.user?.id ?? "",
          },
        },
      });

      filter(Roles.User, {
        $or: [
          {
            managers: {
              $some: {
                id: remult.user?.id ?? "",
              },
            },
          },
          {
            parent: {
              $is: {
                managers: {
                  $some: {
                    id: remult.user?.id ?? "",
                  },
                },
              },
            },
          },
          {
            parent: {
              $is: {
                parent: {
                  $is: {
                    managers: {
                      $some: {
                        id: remult.user?.id ?? "",
                      },
                    },
                  },
                },
              },
            },
          },
        ],
      });
    },

    allowInsertPerRole(allow) {
      allow(Roles.SuperAdmins, true);
    },

    allowDeleteAndUpdatePerRole(allow) {
      allow(Roles.SuperAdmins, true);
      allow(Roles.User, {
        managers: {
          $some: {
            id: remult.user?.id ?? "",
          },
        },
      });
    },

    async saving(entity) {
      const raw = entity.logoImageId;
      if (!raw || !String(raw).trim()) {
        (entity as { logoImageId: string | null }).logoImageId = null;
      } else {
        entity.logoImageId = String(raw).trim();
      }

      const address = entity.address;
      if (!address || !String(address).trim()) {
        (entity as { address: string | null }).address = null;
      } else {
        entity.address = String(address).trim();
      }

      const hours = entity.operatingHours;
      if (!hours || !String(hours).trim()) {
        (entity as { operatingHours: string | null }).operatingHours = null;
      } else {
        entity.operatingHours = String(hours).trim();
      }

      const level = entity.cooperationLevel;
      if (level != null && (level < 1 || level > 9)) {
        (entity as { cooperationLevel: number | null }).cooperationLevel = null;
      }

      const handledBy = entity.handledBy;
      if (
        handledBy != null &&
        handledBy !== "CALL_CENTER" &&
        handledBy !== "COORDINATORS"
      ) {
        (entity as { handledBy: null }).handledBy = null;
      }

      const handlingManagerId = entity.handlingManagerId;
      if (!handlingManagerId || !String(handlingManagerId).trim()) {
        (entity as { handlingManagerId: string | null }).handlingManagerId =
          null;
      }

      entity.comments = entity.comments?.trim() ? entity.comments.trim() : "";

      const parentId = entity.parentId?.trim() || null;
      entity.parentId = parentId;
      if (parentId) {
        const rows = await rowsForParentCheck(entity.id || null, parentId);
        assertPartnerOrganizationParentChange({
          orgId: entity.id || null,
          parentId,
          rows,
        });
      }
    },

    async deleting(entity) {
      const childCount = await remult.repo(PartnerOrganization).count({
        parentId: entity.id,
      });
      if (childCount > 0) {
        throw new Error(PARTNER_ORGANIZATION_HAS_CHILDREN_ERROR);
      }
    },
  };
